import { ChangeDetectionStrategy, Component, ElementRef, inject, signal } from '@angular/core';
import { combineLatest, shareReplay } from 'rxjs';
import { map } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import {
  ActivitySectionDirective,
  HappyHoursService,
  IMenuWithIcon,
  IMenuWithPages,
  MenusService,
  UserActivityClickTrackingDirective,
} from '@core';
import { LoadingComponent } from '@app/components/loading/loading.component';
import { PageHeaderBackSwipeConfig, PageHeaderComponent } from '@app/components/page-header/page-header.component';
import { Router } from '@angular/router';
import { DetailComponent } from '@app/pages/main-page/menu/detail/detail.component';
import { animate, style, transition, trigger } from '@angular/animations';

@Component({
  selector: 'app-page-menu',
  templateUrl: 'menu.component.html',
  styleUrl: 'menu.component.scss',
  imports: [
    LoadingComponent,
    UserActivityClickTrackingDirective,
    ActivitySectionDirective,
    PageHeaderComponent,
    DetailComponent,
  ],
  host: {
    'class': 'page',
  },
  animations: [
    trigger('InOutAnimation', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('400ms ease-in-out', style({ transform: 'translateX(0%)' })),
      ]),
      transition(':leave', [
        animate('400ms ease-in-out', style({ transform: 'translateX(100%)' })),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuComponent {

  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly menusService = inject(MenusService);
  private readonly happyHoursService = inject(HappyHoursService);

  protected readonly backSwipeConfig: PageHeaderBackSwipeConfig = {
    direction: 'vertical',
    element: this.elementRef.nativeElement,
  };

  public readonly currentLanguage = toSignal(this.translate.onLangChange.pipe(
    map((event) => {
      return event.lang;
    }),
  ), { initialValue: this.translate.getCurrentLang() });

  public readonly menusWithPagesAndIcons$ = combineLatest([
    this.menusService.menusWithPagesAndIcons$,
    this.happyHoursService.isNow$,
  ]).pipe(
    map(([menus, isNow]) => {
      if (menus === undefined) {
        return undefined;
      }

      return this.prepare(menus, isNow);
    }),
    shareReplay(1),
  );

  public readonly menus = toSignal(this.menusWithPagesAndIcons$);

  public readonly isLoading = toSignal(this.menusService.loading$, {
    requireSync: true,
  });

  public readonly isSyncInProgress = toSignal(this.menusService.sync$, {
    requireSync: true,
  });

  public readonly currentMenu = signal<IMenuWithPages | null>(null);

  private prepare<T extends IMenuWithPages | IMenuWithIcon>(menus: T[], isNow: boolean): T[] {
    return menus.filter((menu: T) => {
      return !menu.hidden &&
        (
          menu.language === this.currentLanguage() || menu.language === null
        );
    }).sort(
      (a, b) => {

        if (isNow) {
          if (a.happyHours && !b.happyHours) {
            return -1;
          }
          if (!a.happyHours && b.happyHours) {
            return 1;
          }
        }
        else {
          if (a.happyHours && !b.happyHours) {
            return 1;
          }
          if (!a.happyHours && b.happyHours) {
            return -1;
          }
        }

        return a.id - b.id;
      });
  }

  public backToHome(): void {
    void this.router.navigate(['/main', 'home']);
  }
}
